import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  CalendarDaysIcon,
  UserGroupIcon,
  BellIcon,
  UserCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../Authentication/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import { toast } from "sonner";
import LogoutConfirmationModal from "../Authentication/LogoutConfirmationModal"; // Ensure this import path is correct
import { useDispatch, useSelector } from "react-redux";
import { useGetVendorKYCStatusQuery } from "../../redux/service/vendorService";
import { setVendorIsApproved } from "../../redux/slice/upSwapSlice";
import { BsQrCodeScan } from "react-icons/bs";
import DealQRScanner from "../Vendor/VendorDeal/DealQRScanner/DealQRScanner";
import { MdManageAccounts } from "react-icons/md";
import { BookOpenIcon } from "lucide-react";
import { useIsMobile } from "./UserHeader";
import MobileVendorHeader from "./MobileVendorHeader";

const Maps_API_KEY = "AIzaSyCRQXge0eI79YVPiihiUrqOKv4NapMnA9I"; // Ensure this API key is correctly managed

const VendorHeader = () => {
  const { logout, userRole, setUserRole } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false); // New state for logout confirmation modal
  const [isOpenQrScan, setIsOpenQrScan] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({
    city: "",
    state: "",
    area: "",
    country: "",
    latitude: null,
    longitude: null,
  });
  const [manualLocationInput, setManualLocationInput] = useState("");
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const locationInputModalRef = useRef(null);
  const autocompleteInstance = useRef(null);
  const vendorId = localStorage.getItem("vendor_id");

  const handleLocationClick = () => {
    setShowLocationModal(true);
    setManualLocationInput("");
  };

  const handleCloseLocationModal = () => {
    setShowLocationModal(false);
  };

  const initMap = useCallback(() => {
    setIsGoogleMapsLoaded(true);
  }, []);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleMapsLoaded(true);
      return;
    }
    window.initMap = initMap;
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js"]`
    );
    if (existingScript) {
      existingScript.onload = () => setIsGoogleMapsLoaded(true);
      existingScript.onerror = () =>
        toast.error("Failed to load Google Maps API.");
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${Maps_API_KEY}&libraries=places&callback=initMap`;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-script-header";
    script.onerror = () => {
      toast.error("Failed to load Google Maps API for location services.");
    };
    document.head.appendChild(script);
    return () => {
      if (window.initMap === initMap) {
        delete window.initMap;
      }
    };
  }, [initMap]);

  useEffect(() => {
    if (
      isGoogleMapsLoaded &&
      showLocationModal &&
      locationInputModalRef.current
    ) {
      if (!autocompleteInstance.current) {
        try {
          autocompleteInstance.current =
            new window.google.maps.places.Autocomplete(
              locationInputModalRef.current,
              { types: ["(cities)"] }
            );
          autocompleteInstance.current.addListener("place_changed", () => {
            const place = autocompleteInstance.current.getPlace();
            if (place.geometry && place.formatted_address) {
              let city = "Unknown City";
              let state = "";
              let area = "";
              let country = "";
              let latitude = place.geometry.location.lat();
              let longitude = place.geometry.location.lng();
              const addressComponents = place.address_components;
              addressComponents.forEach((component) => {
                if (component.types.includes("locality")) {
                  city = component.long_name;
                } else if (component.types.includes("sublocality_level_1")) {
                  area = component.long_name;
                } else if (
                  component.types.includes("administrative_area_level_1")
                ) {
                  state = component.long_name;
                } else if (component.types.includes("country")) {
                  country = component.long_name;
                }
              });
              setCurrentLocation({
                city,
                state,
                area,
                country,
                latitude,
                longitude,
              });
              localStorage.setItem(
                "userLocation",
                JSON.stringify({
                  city,
                  state,
                  area,
                  country,
                  latitude,
                  longitude,
                })
              );
              setManualLocationInput(place.formatted_address);
              setShowLocationModal(false);
              toast.success("Location updated!");
            } else {
              toast.warning("No details available for the selected location.");
              handleManualLocationSubmit(true);
            }
          });
        } catch (error) {
          console.error("Error initializing Google Autocomplete:", error);
          toast.error(
            "Failed to initialize location suggestions. Please try manual entry."
          );
        }
      }
    }
    return () => {
      if (autocompleteInstance.current) {
        if (window.google && window.google.maps && window.google.maps.event) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteInstance.current
          );
        }
        autocompleteInstance.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapsLoaded, showLocationModal]);

  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      setCurrentLocation(JSON.parse(storedLocation));
    } else if (navigator.geolocation && isGoogleMapsLoaded) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const locationDetails = await getAddressFromCoordinates(
            latitude,
            longitude
          );
          if (locationDetails) {
            setCurrentLocation(locationDetails);
            localStorage.setItem(
              "userLocation",
              JSON.stringify(locationDetails)
            );
          }
        },
        (error) => {
          toast.error("Failed to fetch your location.");
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapsLoaded]);

  // const getAddressFromCoordinates = async (latitude, longitude) => {
  //   if (!isGoogleMapsLoaded || !window.google || !window.google.maps) {
  //     toast.error("Google Maps API not fully loaded. Please try again.");
  //     return null;
  //   }
  //   const geocoder = new window.google.maps.Geocoder();
  //   const latlng = { lat: latitude, lng: longitude };
  //   try {
  //     const { results } = await geocoder.geocode({ location: latlng });
  //     if (results && results.length > 0) {
  //       let city = "Unknown City";
  //       let state = "Unknown Region";
  //       let area = "";
  //       let country = "Unknown Country";
  //       const addressComponents = results[0].address_components;
  //       addressComponents.forEach((component) => {
  //         if (component.types.includes("locality")) {
  //           city = component.long_name;
  //         } else if (component.types.includes("sublocality_level_1")) {
  //           area = component.long_name;
  //         } else if (component.types.includes("administrative_area_level_1")) {
  //           state = component.long_name;
  //         } else if (component.types.includes("country")) {
  //           country = component.long_name;
  //         }
  //       });
  //       const displayState = state !== "Unknown Region" ? state : country;
  //       return {
  //         city,
  //         state: displayState,
  //         area,
  //         country,
  //         latitude,
  //         longitude,
  //       };
  //     } else {
  //       toast.warning(
  //         "Could not find address details for your current location."
  //       );
  //       return {
  //         city: "Location Found",
  //         state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
  //         area: "",
  //         country: "",
  //         latitude,
  //         longitude,
  //       };
  //     }
  //   } catch (error) {
  //     // console.error("Error reverse geocoding with Google Maps:", error);
  //     // toast.error(
  //     //   "Failed to fetch address details. Check console for more info."
  //     // );
  //     toast.error(
  //       "Something went wrong while fetching your address. Please try again later."
  //     );
  //     return null;
  //   }
  // };

  const getAddressFromCoordinates = async (latitude, longitude) => {
    if (!isGoogleMapsLoaded || !window.google || !window.google.maps) {
      toast.error(
        "Location services are still loading. Please wait a moment and try again."
      );
      return null;
    }

    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    try {
      const { results } = await geocoder.geocode({ location: latlng });

      if (results && results.length > 0) {
        let city = "Unknown City";
        let state = "Unknown Region";
        let area = "";
        let country = "Unknown Country";

        const addressComponents = results[0].address_components;
        addressComponents.forEach((component) => {
          if (component.types.includes("locality")) {
            city = component.long_name;
          } else if (component.types.includes("sublocality_level_1")) {
            area = component.long_name;
          } else if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (component.types.includes("country")) {
            country = component.long_name;
          }
        });

        const displayState = state !== "Unknown Region" ? state : country;

        return {
          city,
          state: displayState,
          area,
          country,
          latitude,
          longitude,
        };
      } else {
        toast.warning(
          "We couldn't find a precise address for your location. Showing approximate coordinates instead."
        );
        return {
          city: "Location Found",
          state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
          area: "",
          country: "",
          latitude,
          longitude,
        };
      }
    } catch (error) {
      toast.error(
        "Something went wrong while fetching your address. Please try again later."
      );
      console.error("Google Maps reverse geocoding error:", error);
      return null;
    }
  };

  // Function to get current geolocation and then reverse geocode it
  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }
    if (!isGoogleMapsLoaded) {
      toast.info("Loading location services... please try again shortly.");
      return;
    }

    toast.info("Fetching your current location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const locationDetails = await getAddressFromCoordinates(
          latitude,
          longitude
        );

        if (locationDetails) {
          setCurrentLocation(locationDetails);
          localStorage.setItem("userLocation", JSON.stringify(locationDetails));
          toast.success("Location updated successfully!");
        } else {
          setCurrentLocation({
            city: "Unknown Address",
            state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
            latitude,
            longitude,
          });
          localStorage.setItem(
            "userLocation",
            JSON.stringify({
              city: "Unknown Address",
              state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(
                4
              )}`,
              latitude,
              longitude,
            })
          );
        }
        setShowLocationModal(false); // Close modal after fetching
      },
      (error) => {
        let errorMessage = "Unable to retrieve your location.";
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage =
            "Location access denied. Please allow location services in your browser settings.";
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = "Location information is unavailable.";
        } else if (error.code === error.TIMEOUT) {
          errorMessage = "The request to get location timed out.";
        }
        console.error("Geolocation error:", error);
        toast.error(errorMessage);
      }
    );
  };

  // Function to handle manual location submission
  const handleManualLocationSubmit = (fromAutocompleteFallback = false) => {
    if (manualLocationInput.trim() === "") {
      if (!fromAutocompleteFallback) {
        toast.warning("Please enter a location.");
      }
      return;
    }

    const parts = manualLocationInput.split(",").map((p) => p.trim());
    const city = parts[0] || manualLocationInput.trim();
    const state = parts.length > 1 ? parts.slice(1).join(", ") : "";

    setCurrentLocation({
      city: city,
      state: state,
      latitude: null,
      longitude: null,
    });
    localStorage.setItem(
      "userLocation",
      JSON.stringify({
        city: city,
        state: state,
        latitude: null,
        longitude: null,
      })
    );
    setShowLocationModal(false); // Close modal after submission
    if (!fromAutocompleteFallback) {
      toast.success("Manual location set successfully!");
    }
  };

  const navItems = [
    {
      name: "Deals",
      icon: CalendarDaysIcon,
      dropdownItems: [
        // { name: "Browse Deal", path: "/PopularDeals", id: 1 },
        { name: "Create Deal", path: "/vendor/deal/add", id: 2 },
        { name: "My Created Deal", path: "/MyDeals", id: 3 },
      ],
    },

    {
      name: "Services",
      icon: UserGroupIcon,
      dropdownItems: [{ name: "My Services", path: "/vendor/services" }],
    },
    {
      name: "Providers",
      icon: MdManageAccounts,
      dropdownItems: [
        { name: "My Providers", path: "/vendor/providers" },
        { name: "Add Provider", path: "/vendor/providers/add" },
      ],
    },
    {
      name: "Appointment",
      icon: BookOpenIcon,
      dropdownItems: [{ name: "My Appointments", path: "/appointments" }],
    },
  ];

  const handleDropdownToggle = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
    setProfileDropdownOpen(false);
  };

  const handleDropdownItemClick = (item) => {
    if (item.path && !item.commingsoon) {
      navigate(item.path);
      setOpenDropdown(null);
    }
  };

  // Function to show the logout confirmation modal
  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true);
    setProfileDropdownOpen(false);
  };

  // Function to handle actual logout after confirmation
  const confirmLogout = () => {
    logout();
    localStorage.clear();
    navigate("/");
    setUserRole("public");
    setShowLogoutConfirmation(false);
    toast.success("Logged out successfully!");
  };

  // Function to cancel logout (also used for modal close button)
  const cancelLogout = () => {
    setShowLogoutConfirmation(false);
    toast.info("Logout cancelled.");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close navigation dropdowns
      if (!e.target.closest(".nav-dropdown-container")) {
        setOpenDropdown(null);
      }
      // Close profile dropdown, but not if clicking inside logout modal
      if (
        !e.target.closest(".profile-dropdown-container") &&
        !e.target.closest(".logout-modal-container")
      ) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    data: kycData,
    isLoading: kycDataIsLoading,
    isFetching: kycDataIsFetching,
  } = useGetVendorKYCStatusQuery(vendorId, { skip: !vendorId });

  useEffect(() => {
    if (!kycDataIsFetching && !kycDataIsLoading) {
      dispatch(setVendorIsApproved(kycData?.is_approved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycDataIsLoading, kycDataIsFetching, kycData]);

  const { vendorIsApproved } = useSelector((state) => state.upSwap);
  const isMobile = useIsMobile(); // Add this hook from your UserHeader

  if (isMobile) {
    return (
      <>
        <MobileVendorHeader
          navItems={navItems}
          handleLocationClick={handleLocationClick}
          currentLocation={currentLocation}
          setIsOpenQrScan={setIsOpenQrScan}
          vendorIsApproved={vendorIsApproved}
          setShowLocationModal={setShowLocationModal}
          userRole={userRole}
          setUserRole={setUserRole}
          navigate={navigate}
          handleLogoutClick={handleLogoutClick}
          setProfileDropdownOpen={setProfileDropdownOpen}
          profileDropdownOpen={profileDropdownOpen}
        />

        {/* Location Modal */}
        {showLocationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-sm mx-4 md:max-w-md md:p-8">
              {/* Close Icon in top-right corner */}
              <button
                onClick={handleCloseLocationModal}
                className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="Close location selection"
              >
                <FaTimes size={20} />
              </button>

              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Select Your Location
              </h2>

              {/* Use Current Location Button */}
              <button
                onClick={handleUseCurrentLocation}
                disabled={!isGoogleMapsLoaded}
                className={`w-full py-2 px-4 rounded-md transition mb-4 ${
                  !isGoogleMapsLoaded
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                <FaMapMarkerAlt className="inline-block mr-2" />
                {isGoogleMapsLoaded
                  ? "Use My Current Location"
                  : "Loading Location Services..."}
              </button>

              <div className="text-center text-gray-500 mb-4 font-semibold">
                - OR -
              </div>

              <input
                type="text"
                ref={locationInputModalRef}
                placeholder="Enter location manually (e.g., New York, USA)"
                className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                value={manualLocationInput}
                onChange={(e) => setManualLocationInput(e.target.value)}
              />
              <button
                onClick={() => handleManualLocationSubmit()}
                className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition mb-4"
              >
                Set Location
              </button>
            </div>
          </div>
        )}

        {showLogoutConfirmation && (
          <LogoutConfirmationModal
            onConfirm={confirmLogout}
            onCancel={cancelLogout}
            onClose={cancelLogout}
          />
        )}

        <DealQRScanner
          isOpen={isOpenQrScan}
          onClose={() => setIsOpenQrScan(false)}
        />
      </>
    );
  }

  return (
    <header className="bg-white shadow-sm py-4 px-6 border-b border-gray-200">
      <div className="container mx-auto flex items-center justify-between">
        {/* Logo */}
        <div className="flex-shrink-0 cursor-pointer">
          <img
            src="/upswap.png"
            alt="UpSwap"
            className="h-10"
            onClick={() => navigate("/vendor/dashboard")}
          />
        </div>

        {/* Location Display */}
        <div
          className="flex items-center text-gray-700 hover:text-black text-sm font-medium cursor-pointer ml-4"
          onClick={handleLocationClick}
        >
          <FaMapMarkerAlt className="h-5 w-5 text-gray-500 mr-2" />
          <div className="flex flex-col">
            <div className="font-semibold leading-none">
              {currentLocation.city}
            </div>
            <div className="text-xs text-gray-500 leading-none">
              {currentLocation.state}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="hidden lg:flex flex-grow justify-center space-x-8">
          {navItems.map((item, index) => (
            <div
              key={index}
              className="relative flex items-center text-gray-700 hover:text-black text-sm font-medium cursor-pointer nav-dropdown-container"
              onClick={() => handleDropdownToggle(index)}
            >
              {item.icon && <item.icon className="h-5 w-5 mr-1" />}
              {item.name}
              <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />

              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
                  openDropdown === index ? "flex flex-col gap-1" : "hidden"
                }`}
              >
                {item.dropdownItems.map((dItem, dIndex) =>
                  !vendorIsApproved && dItem?.id === 2 ? (
                    <div key={dIndex} className="hidden"></div>
                  ) : (
                    <Link
                      key={dIndex}
                      to={dItem.commingsoon ? "#" : dItem.path}
                      className={`block px-4 py-2 text-sm relative  rounded-sm ${
                        dItem.commingsoon
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!dItem.commingsoon);
                        handleDropdownItemClick(dItem);
                      }}
                    >
                      {dItem.commingsoon && (
                        <span className="absolute top-0 left-4 text-xs text-orange-500 font-semibold">
                          Coming Soon
                        </span>
                      )}
                      <span className={dItem.commingsoon ? "pt-1 block" : ""}>
                        {dItem.name}
                      </span>
                    </Link>
                  )
                )}
              </div>
            </div>
          ))}
        </nav>
        <button
          onClick={() => {
            setIsOpenQrScan(true);
          }}
          className="bg-orange-500 text-white px-2 py-1 rounded hover:bg-orange-600 transition mr-2 flex gap-2 items-center"
        >
          <BsQrCodeScan className="font-bold" />
          Scan
        </button>

        {vendorIsApproved && (
          <button className="relative flex items-center px-1 py-2 rounded-full bg-gray-100/80 hover:bg-gray-100 transition-all duration-300 mr-8">
            <div
              className={`absolute h-7 w-[90px] top-1 transition-all duration-500 ease-in-out ${
                userRole === "vendor" ? "left-2" : "left-[75px]"
              }`}
            >
              <div className="h-full w-[67px] rounded-full bg-white shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] border border-gray-100" />
            </div>
            <div className="relative z-10 flex items-center">
              <span
                onClick={() => {
                  localStorage.setItem("userRole", "vendor");
                  setUserRole("vendor");
                  // Optional: navigate to vendor dashboard if not already there
                  if (window.location.pathname !== "/vendor/dashboard") {
                    navigate("/vendor/dashboard");
                  }
                }}
                className={`px-4  text-sm font-medium transition-colors duration-300 ${
                  userRole === "vendor"
                    ? "text-orange-600"
                    : "text-gray-400 hover:text-gray-500"
                }`}
              >
                Vendor
              </span>
              <span
                onClick={() => {
                  localStorage.setItem("userRole", "user");
                  setUserRole("user");
                  navigate("/LocalActivities");
                }}
                className={`px-4  text-sm font-medium transition-colors duration-300 ${
                  userRole === "user"
                    ? "text-orange-600"
                    : "text-gray-400 hover:text-gray-500"
                }`}
              >
                User
              </span>
            </div>
          </button>
        )}

        {/* Right Side Icons */}
        <div className="flex items-center space-x-4 ml-auto">
          <div
            className="relative cursor-pointer text-gray-600 hover:text-black"
            onClick={() => toast.info("Notifications coming soon!")}
          >
            <BellIcon className="h-6 w-6" />
            <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 border border-white rounded-full" />
          </div>

          <div
            className="relative cursor-pointer text-gray-600 hover:text-black flex items-center profile-dropdown-container"
            onClick={() => {
              setProfileDropdownOpen(!profileDropdownOpen);
              setOpenDropdown(null); // Close other dropdowns
            }}
          >
            <UserCircleIcon className="h-7 w-7" />
            <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />

            <div
              className={`absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
                profileDropdownOpen ? "block" : "hidden"
              }`}
            >
              <button
                onClick={() => {
                  navigate("/BasicInfo");
                  setProfileDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Vendor Profile
              </button>
              <button
                onClick={() => {
                  navigate("/mySalesDeal");
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                My Sales
              </button>
              <button
                onClick={handleLogoutClick}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Location Modal */}
        {showLocationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-sm mx-4 md:max-w-md md:p-8">
              {/* Close Icon in top-right corner */}
              <button
                onClick={handleCloseLocationModal}
                className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="Close location selection"
              >
                <FaTimes size={20} />
              </button>

              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Select Your Location
              </h2>

              {/* Use Current Location Button */}
              <button
                onClick={handleUseCurrentLocation}
                disabled={!isGoogleMapsLoaded}
                className={`w-full py-2 px-4 rounded-md transition mb-4 ${
                  !isGoogleMapsLoaded
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                <FaMapMarkerAlt className="inline-block mr-2" />
                {isGoogleMapsLoaded
                  ? "Use My Current Location"
                  : "Loading Location Services..."}
              </button>

              <div className="text-center text-gray-500 mb-4 font-semibold">
                - OR -
              </div>

              <input
                type="text"
                ref={locationInputModalRef}
                placeholder="Enter location manually (e.g., New York, USA)"
                className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                value={manualLocationInput}
                onChange={(e) => setManualLocationInput(e.target.value)}
              />
              <button
                onClick={() => handleManualLocationSubmit()}
                className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition mb-4"
              >
                Set Location
              </button>
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutConfirmation && (
          <LogoutConfirmationModal
            onConfirm={confirmLogout}
            onCancel={cancelLogout}
            onClose={cancelLogout}
          />
        )}
        <DealQRScanner
          isOpen={isOpenQrScan}
          onClose={() => setIsOpenQrScan(false)}
        />
      </div>
    </header>
  );
};

export default VendorHeader;
